import React, { useState, useEffect } from "react";
import { useParams, withRouter } from "react-router";
import { useSelector } from "react-redux";
import {
  getEmailCampaignById,
  getEmailCampaignMetadata,
  updateEmailCampaignById,
} from "@api/email/emailCampaign";
import { getActiveUserEmails } from "@api/email/users/userEmail";
import { searchUsers, getUserCountByDateRange } from "@api/user/superAdmin";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  Upload,
  message,
  Modal,
  Table,
  Space,
  Tag,
  Card,
  Divider,
  Typography,
  Empty,
  Badge,
  Spin,
  Alert,
} from "antd";
import {
  SuccessNotification,
  ErrorNotification,
  EmailVolumeControl,
} from "@components";
import moment from "moment";
import * as XLSX from "xlsx";
import {
  InboxOutlined,
  DeleteOutlined,
  PlusOutlined,
  MailOutlined,
  UserOutlined,
  InfoCircleOutlined,
  CodeOutlined,
  EditOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import Papa from "papaparse";

const { Option } = Select;
const { Dragger } = Upload;
const { Text } = Typography;
const { RangePicker } = DatePicker;

// Initial state for the email campaign form
const initialState = {
  campaignType: "all",
  name: "",
  fromName: "", // Add fromName
  subject: "",
  htmlTemplate: "",
  scheduledFor: null,
  status: "draft",
  includedPlans: [],
  targetedUsers: [],
  externalRecipients: [],
  dateRangeUsers: [],
  emailsToUse: [],
  replyTo: null, // Add replyTo
  cc: [], // Add CC field
  bcc: [],
  variables: [],
  emailSendingVolume: 1,
  dateRangeFilter: {
    startDate: null,
    endDate: null,
  },
};

const EmailCampaignUpdateForm = ({ history }) => {
  const { id } = useParams();
  const { user } = useSelector((state) => state.auth);

  const [values, setValues] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [excludedUsers, setExcludedUsers] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [fileRecipients, setFileRecipients] = useState({});
  const [availableEmails, setAvailableEmails] = useState({});
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [editingVariable, setEditingVariable] = useState(null);
  const [timezone, setTimezone] = useState(moment.tz.guess());
  const [variableModalVisible, setVariableModalVisible] = useState(false);
  const [availableColumns, setAvailableColumns] = useState([
    { key: "email", title: "Email", required: true },
    { key: "name", title: "Name", required: false },
  ]);
  const [newRecipientData, setNewRecipientData] = useState({
    email: "",
    name: "",
  });
  const [recipientColumns, setRecipientColumns] = useState(
    new Set(["email", "name"])
  );
  // Date range filter
  const [dateRangeUsers, setDateRangeUsers] = useState([]);
  const [fetchingUserCount, setFetchingUserCount] = useState(false);

  useEffect(() => {
    fetchEmailCampaign();
    fetchEmailCampaignMetadata();
    fetchAvailableEmails();
  }, []);

  // Function to format timezone with GMT offset
  const formatTimezone = (tz) => {
    const offset = moment.tz(tz).utcOffset();
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    const sign = offset >= 0 ? "+" : "-";
    return `${tz} (GMT${sign}${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")})`;
  };

  // Fetch email campaign by id
  const fetchEmailCampaign = async () => {
    setLoading(true);
    try {
      const { data } = await getEmailCampaignById(id);

      // Handle scheduledFor field
      if (data.scheduledFor) {
        data.scheduledFor = moment(data.scheduledFor);
      }

      // Map emailsToUse for UI representation
      const formattedSelectedEmails = data.emailsToUse.map((email) => {
        if (email.useAlias) {
          return `alias:${email.email}:${email.selectedAlias}`;
        }
        return `email:${email.email}`;
      });

      setSelectedEmails(formattedSelectedEmails);

      // Format targetedUsers for Select component
      if (data.campaignType === "targeted" && data.targetedUsers?.length > 0) {
        const formattedUsers = data.targetedUsers.map((user) => ({
          value: user._id,
          label: `${user.email} (${user.name})`,
          email: user.email,
        }));
        setUserOptions((prevOptions) => [...prevOptions, ...formattedUsers]);
        data.targetedUsers = data.targetedUsers.map((user) => user._id); // Keep only IDs
      }

      // Format dateRangeUsers for display
      if (
        data.campaignType === "dateRange" &&
        data.dateRangeUsers?.length > 0
      ) {
        setDateRangeUsers(
          data.dateRangeUsers.map((user) => ({
            _id: user.user,
            name: user.metadata?.name || "",
            email: user.metadata?.email || "",
            joinedAt: user.joinedAt,
          }))
        );
      }

      // Handle externalRecipients and dynamic columns
      if (data.externalRecipients?.length > 0) {
        const existingColumns = new Set(["email"]);
        const firstRecipient = data.externalRecipients[0];

        if (firstRecipient.values) {
          Object.keys(firstRecipient.values).forEach((key) => {
            existingColumns.add(key);
          });
        }

        setRecipientColumns(existingColumns);

        const updatedColumns = [
          { key: "email", title: "Email", required: true },
          ...Array.from(existingColumns)
            .filter((key) => key !== "email")
            .map((key) => ({ key, title: key, required: false })),
        ];
        setAvailableColumns(updatedColumns);

        setNewRecipientData(
          Array.from(existingColumns).reduce(
            (acc, column) => ({ ...acc, [column]: "" }),
            {}
          )
        );
      }

      // Add emailVolumeInfo to values
      setValues({
        ...data,
        emailVolumeInfo: {
          sendRate: data.sendRate || 25,
          totalEmails: data.totalEmails || data.externalRecipients?.length || 0,
          timeToSend: Math.ceil(
            (data.totalEmails || data.externalRecipients?.length || 0) /
              (data.sendRate || 25)
          ),
        },
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("GET_EMAIL_CAMPAIGN_BY_ID_ERROR", error);
      ErrorNotification("Error fetching email campaign");
    }
  };

  // Add this function with your other fetch functions
  const fetchAvailableEmails = async () => {
    try {
      const response = await getActiveUserEmails();
      // Group emails by domain
      const emailsByDomain = response.data.reduce((acc, email) => {
        const domain = email.email.split("@")[1];
        if (!acc[domain]) {
          acc[domain] = [];
        }
        acc[domain].push(email);
        return acc;
      }, {});
      setAvailableEmails(emailsByDomain);
    } catch (error) {
      console.error("Error fetching available emails:", error);
      ErrorNotification("Failed to fetch available emails");
    }
  };

  // Function to fetch email campaign metadata
  const fetchEmailCampaignMetadata = async () => {
    try {
      const response = await getEmailCampaignMetadata();
      setPlans(response.data.plans);
      setTemplates(response.data.htmlTemplates);
    } catch (error) {
      console.error("Error fetching email campaign metadata:", error);
      ErrorNotification("Failed to fetch email campaign metadata");
    }
  };

  // Add this function to handle date range changes
  const handleDateRangeChange = async (dates) => {
    if (!dates || dates.length !== 2) {
      setValues({
        ...values,
        dateRangeFilter: {
          startDate: null,
          endDate: null,
        },
      });
      setDateRangeUsers([]);
      return;
    }

    const [startDate, endDate] = dates;

    setValues({
      ...values,
      dateRangeFilter: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      },
    });

    // Fetch users for selected date range
    setFetchingUserCount(true);
    try {
      const response = await getUserCountByDateRange({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });
      setDateRangeUsers(response.data.users);
    } catch (error) {
      console.error("Error fetching users:", error);
      ErrorNotification("Failed to fetch users for date range");
      setDateRangeUsers([]);
    } finally {
      setFetchingUserCount(false);
    }
  };

  // Add helper function for email display formatting
  const formatEmailDisplay = (email) => {
    return `${email.name || email.email.split("@")[0]} <${email.email}>`;
  };

  const formatEmailsForSelect = () => {
    return Object.entries(availableEmails).map(([domain, emails]) => {
      const options = emails
        .map((email) => {
          // Base email option
          const baseOption = {
            value: `email:${email._id}`,
            label: (
              <Space>
                <MailOutlined />
                <span>{email.name || email.email.split("@")[0]}</span>
                <Text type="secondary">&lt;{email.email}&gt;</Text>
              </Space>
            ),
            email: email,
          };

          // Alias options for this email
          const aliasOptions = (email.aliases || []).map((alias) => ({
            value: `alias:${email._id}:${alias}`,
            label: (
              <Space style={{ paddingLeft: 24 }}>
                <UserOutlined />
                <span>{alias.split("@")[0]}</span>
                <Text type="secondary">&lt;{alias}&gt;</Text>
                <Badge
                  count="Alias"
                  style={{
                    backgroundColor: "#e6f4ff",
                    color: "#1890ff",
                    fontSize: "12px",
                    border: "1px solid #91caff",
                  }}
                />
              </Space>
            ),
            parentEmail: email,
            isAlias: true,
          }));

          return [baseOption, ...aliasOptions];
        })
        .flat();

      return {
        label: (
          <Space>
            <span>Domain: {domain}</span>
            <Text type="secondary">
              ({emails.length} email{emails.length !== 1 ? "s" : ""})
            </Text>
          </Space>
        ),
        options,
      };
    });
  };

  const handleEmailSelection = (selectedValues) => {
    const formattedEmails = selectedValues.map((value) => {
      const [type, emailId, alias] = value.split(":");

      return {
        email: emailId,
        useAlias: type === "alias",
        selectedAlias: type === "alias" ? alias : null,
      };
    });

    setSelectedEmails(selectedValues);
    setValues({ ...values, emailsToUse: formattedEmails });
  };

  const getSelectedEmailDetails = () => {
    const details = {
      totalEmails: selectedEmails.length,
      baseEmails: 0,
      aliases: 0,
      domains: new Set(),
    };

    selectedEmails.forEach((value) => {
      const [type, emailId, alias] = value.split(":");
      if (type === "email") {
        details.baseEmails++;
        const email = Object.values(availableEmails)
          .flat()
          .find((e) => e._id === emailId);
        if (email) {
          details.domains.add(email.email.split("@")[1]);
        }
      } else {
        details.aliases++;
        details.domains.add(alias.split("@")[1]);
      }
    });

    return details;
  };

  // Handle form changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
  };

  // Handle campaign type change
  const handleCampaignTypeChange = (value) => {
    setValues({
      ...values,
      campaignType: value,
      includedPlans: [],
      targetedUsers: [],
      externalRecipients: [],
    });
    setExcludedUsers([]);
    setFileRecipients({});
  };

  // Handle user search for exclusion or targeting
  const handleUserSearch = async (value) => {
    if (value.length > 2) {
      setSearching(true);
      try {
        const response = await searchUsers(value);
        const options = response.data.map((user) => ({
          value: user._id,
          label: `${user.email} (${user.name})`,
          email: user.email,
        }));
        setUserOptions(options);
      } catch (error) {
        console.error("Error searching users:", error);
      } finally {
        setSearching(false);
      }
    } else {
      setUserOptions([]);
    }
  };

  // Handle changes in excluded users
  const handleExcludedUsersChange = (selectedUserIds) => {
    const updatedExcludedUsers = selectedUserIds
      .map((id) => {
        const user = [...excludedUsers, ...userOptions].find(
          (u) => u.value === id
        );
        return user
          ? { value: user.value, label: user.label, email: user.email }
          : null;
      })
      .filter(Boolean);
    setExcludedUsers(updatedExcludedUsers);
  };

  // Handle changes in targeted users
  const handleTargetedUsersChange = (selectedUserIds) => {
    setValues({
      ...values,
      targetedUsers: selectedUserIds,
    });
  };

  // Enhanced function to parse emails from various formats
  const parseCSVData = (data) => {
    if (!data || data.length === 0) return [];

    // Get headers from first row, creating a mapping between display name and key
    const headers = data[0].map((header) => {
      const displayName = header.trim(); // Keep original for display
      const key = header.toLowerCase().trim(); // Simple lowercase for key
      return {
        key,
        title: displayName,
        required: key === "email",
      };
    });

    // Update available columns with the new mapping
    const newColumns = new Set([...recipientColumns]);
    headers.forEach((header) => newColumns.add(header.key));
    setRecipientColumns(newColumns);
    setAvailableColumns((prevColumns) => {
      const existingKeys = new Set(prevColumns.map((col) => col.key));
      const newHeaders = headers.filter(
        (header) => !existingKeys.has(header.key)
      );
      return [...prevColumns, ...newHeaders];
    });

    // Parse rows into objects
    const recipients = [];
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      const recipient = {};

      headers.forEach((header, index) => {
        const value = row[index]?.toString().trim() || "";
        if (value) {
          recipient[header.key] = value;
        }
      });

      // Only add if email is present and valid
      if (recipient.email && isValidEmail(recipient.email)) {
        recipients.push(recipient);
      }
    }

    return recipients;
  };

  // Handle file upload
  const handleFileUpload = async (info) => {
    const { file, fileList } = info;

    if (file.status === "removed") {
      // Remove recipients associated with the deleted file
      const updatedFileRecipients = { ...fileRecipients };
      delete updatedFileRecipients[file.name];
      setFileRecipients(updatedFileRecipients);

      // Recalculate externalRecipients
      const allRecipients = Object.values(updatedFileRecipients).flat();
      setValues({ ...values, externalRecipients: allRecipients });

      message.info(`File ${file.name} removed. Recipient list updated.`);
      return;
    }

    // Process new files
    for (let i = 0; i < fileList.length; i++) {
      const currentFile = fileList[i];
      if (currentFile.status !== "done") {
        await processFile(currentFile);
      }
    }
  };

  const processFile = (file) => {
    return new Promise((resolve, reject) => {
      const fileObj = file.originFileObj || file;

      if (fileObj instanceof Blob) {
        const reader = new FileReader();

        reader.onload = async (e) => {
          const content = e.target.result;
          let parsedData;

          try {
            if (file.name.endsWith(".csv")) {
              parsedData = Papa.parse(content, {
                skipEmptyLines: true,
                header: false,
              }).data;
              const recipients = parseCSVData(parsedData);
              handleParsedRecipients(recipients, file.name);
            } else if (file.name.match(/\.(xlsx|xls)$/i)) {
              const workbook = XLSX.read(content, { type: "array" });
              const firstSheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[firstSheetName];
              parsedData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
              const recipients = parseCSVData(parsedData);
              handleParsedRecipients(recipients, file.name);
            } else if (file.name.endsWith(".txt")) {
              const lines = content
                .split(/\r?\n/)
                .filter((line) => line.trim());
              let recipients = [];

              const firstLine = lines[0].trim();
              const possibleHeaders = firstLine.split(/[,\t]/);

              if (
                possibleHeaders.length > 1 &&
                possibleHeaders.some((h) => h.toLowerCase().includes("email"))
              ) {
                parsedData = lines.map((line) => line.split(/[,\t]/));
                recipients = parseCSVData(parsedData);
              } else {
                recipients = lines
                  .filter((line) => isValidEmail(line.trim()))
                  .map((line) => ({
                    email: line.trim(),
                    name: "",
                  }));
              }
              handleParsedRecipients(recipients, file.name);
            }

            resolve();
          } catch (error) {
            console.error("Error processing file:", error);
            message.error(
              `Failed to process ${file.name}. Please check the file format.`
            );
            reject(error);
          }
        };

        reader.onerror = (error) => {
          console.error("Error reading file:", error);
          message.error(
            `Failed to read the file ${file.name}. Please try again.`
          );
          reject(error);
        };

        if (file.name.match(/\.(xlsx|xls)$/i)) {
          reader.readAsArrayBuffer(fileObj);
        } else {
          reader.readAsText(fileObj);
        }
      } else {
        console.error("Invalid file object:", fileObj);
        message.error(`Invalid file ${file.name}. Please try uploading again.`);
        reject(new Error("Invalid file object"));
      }
    });
  };

  // Helper function to handle parsed recipients
  const handleParsedRecipients = (recipients, fileName) => {
    // Format recipients according to the schema structure
    const formattedRecipients = recipients.map((recipient) => {
      // Extract email field
      const { email, ...otherFields } = recipient;

      // Create values Map with remaining fields
      const values = new Map();
      Object.entries(otherFields).forEach(([key, value]) => {
        if (value) {
          // Only add non-empty values
          values.set(key, value);
        }
      });

      return {
        email: email.toLowerCase(),
        values: Object.fromEntries(values), // Convert Map to object for API
      };
    });

    setFileRecipients((prev) => ({
      ...prev,
      [fileName]: formattedRecipients,
    }));

    setValues((prevValues) => {
      const allRecipients = [
        ...Object.values({
          ...fileRecipients,
          [fileName]: formattedRecipients,
        }).flat(),
      ];

      // Deduplicate based on email
      const uniqueRecipients = allRecipients.reduce((acc, current) => {
        const existing = acc.find((item) => item.email === current.email);
        if (!existing) {
          acc.push(current);
        }
        return acc;
      }, []);

      return { ...prevValues, externalRecipients: uniqueRecipients };
    });

    // Initialize newRecipientData with all available columns
    setNewRecipientData((prevData) => {
      const newData = { ...prevData };
      Array.from(recipientColumns).forEach((column) => {
        if (!newData.hasOwnProperty(column)) {
          newData[column] = "";
        }
      });
      return newData;
    });

    message.success(`${recipients.length} recipients loaded from ${fileName}`);
  };

  // Update the external recipients section in the form
  const renderExternalRecipientsSection = () => (
    <Form.Item label="External Recipients">
      <Card>
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          {/* Manual Entry Section */}
          <Card size="small" title="Add Recipients Manually">
            <Space direction="vertical" style={{ width: "100%" }}>
              <Input.TextArea
                placeholder="Enter email addresses (one per line or separated by commas)"
                rows={4}
                onBlur={(e) => {
                  const text = e.target.value;
                  const emails = text
                    .split(/[\n,]/)
                    .map((email) => email.trim())
                    .filter((email) => email && isValidEmail(email));

                  if (emails.length > 0) {
                    const newRecipients = emails.map((email) => ({
                      email: email.toLowerCase(),
                      values: {},
                    }));

                    setValues((prev) => ({
                      ...prev,
                      externalRecipients: [
                        ...prev.externalRecipients,
                        ...newRecipients.filter(
                          (newRec) =>
                            !prev.externalRecipients.some(
                              (existingRec) =>
                                existingRec.email === newRec.email
                            )
                        ),
                      ],
                    }));

                    // Clear the textarea
                    e.target.value = "";

                    message.success(`${emails.length} recipient(s) added`);
                  }
                }}
              />
              <Text type="secondary">
                Tip: You can paste multiple email addresses separated by commas
                or new lines
              </Text>
            </Space>
          </Card>

          {/* File Upload Section */}
          <Card size="small" title="Import Recipients from File">
            <Space direction="vertical" style={{ width: "100%" }}>
              <Alert
                message="Import Recipients"
                description={
                  <ul style={{ margin: "0", paddingLeft: "20px" }}>
                    <li>
                      Upload CSV, Excel (.xlsx, .xls), or text files to import
                      recipients
                    </li>
                    <li>All columns from the files will be preserved</li>
                    <li>
                      The email column is required and must contain valid email
                      addresses
                    </li>
                    <li>
                      Additional columns can be used as variables in your email
                      template
                    </li>
                  </ul>
                }
                type="warning"
                showIcon
              />

              <Dragger
                name="file"
                multiple={true}
                beforeUpload={() => false}
                onChange={handleFileUpload}
                accept=".csv,.txt,.xlsx,.xls"
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag files to this area to upload
                </p>
                <p className="ant-upload-hint">
                  Support for CSV, Excel, or text files with recipients
                </p>
              </Dragger>
            </Space>
          </Card>

          {values.externalRecipients?.length > 0 && (
            <div style={{ marginTop: "16px" }}>
              <Space>
                <Badge count={values.externalRecipients.length} />
                <Text>recipients loaded</Text>
                <Button
                  onClick={() => {
                    setValues({ ...values, externalRecipients: [] });
                    setFileRecipients({});
                  }}
                >
                  Clear All
                </Button>
                <Button type="primary" onClick={showModal}>
                  View & Edit Recipients
                </Button>
              </Space>
            </div>
          )}
        </Space>
      </Card>
    </Form.Item>
  );

  // Add the getReplyToValue helper function
  const getReplyToValue = () => {
    if (!values.replyTo) return undefined;

    // If it's an ID (from original email selection)
    const emailFromId = Object.values(availableEmails)
      .flat()
      .find((e) => e._id === values.replyTo);

    if (emailFromId) {
      return emailFromId.email;
    }

    // If it's already an email address (from manual entry)
    return values.replyTo;
  };

  // Modified renderReplyToSection for update form
  const renderReplyToSection = () => (
    <Form.Item
      label="Reply-To"
      tooltip={{
        title: "Email address that will receive replies to this campaign",
        icon: <InfoCircleOutlined />,
      }}
    >
      <Select
        style={{ width: "100%" }}
        placeholder="Select or enter reply-to email address"
        onChange={(value) => {
          // Handle clearing the value
          if (!value || value.length === 0) {
            setValues({ ...values, replyTo: undefined });
            return;
          }

          // Get the last value if it's an array (from tags mode)
          const actualValue = Array.isArray(value)
            ? value[value.length - 1]
            : value;

          // If the value is a valid email, use it directly
          if (isValidEmail(actualValue)) {
            setValues({ ...values, replyTo: actualValue });
            return;
          }

          // Otherwise, try to find the email object and use its ID
          const emailObj = Object.values(availableEmails)
            .flat()
            .find((e) => e.email === actualValue);

          if (emailObj) {
            setValues({ ...values, replyTo: emailObj._id });
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            const value = e.target.value;
            if (isValidEmail(value)) {
              setValues({ ...values, replyTo: value });
              setDropdownOpen(false);
            }
          }
        }}
        value={getReplyToValue()}
        optionFilterProp="label"
        optionLabelProp="label"
        allowClear
        showSearch
        showArrow
        mode="tags"
        maxTagCount={1}
        tokenSeparators={[","]}
        open={dropdownOpen}
        onDropdownVisibleChange={setDropdownOpen}
        onSearch={(value) => {
          setDropdownOpen(!!value);
        }}
        onBlur={() => {
          const value = getReplyToValue();
          if (value && !isValidEmail(value)) {
            setValues({ ...values, replyTo: undefined });
          }
          setDropdownOpen(false);
        }}
      >
        {/* Add a group for sending emails */}
        <Select.OptGroup label="From Selected Sending Emails">
          {selectedEmails.map((emailValue) => {
            const [type, emailId, alias] = emailValue.split(":");
            const email = Object.values(availableEmails)
              .flat()
              .find((e) => e._id === emailId);

            if (type === "alias") {
              return (
                <Option key={alias} value={alias} label={alias}>
                  <Space>
                    <UserOutlined />
                    <span>{alias.split("@")[0]}</span>
                    <Text type="secondary">&lt;{alias}&gt;</Text>
                    <Badge
                      count="Alias"
                      style={{ backgroundColor: "#e6f4ff", color: "#1890ff" }}
                    />
                  </Space>
                </Option>
              );
            }

            return email ? (
              <Option
                key={email.email}
                value={email.email}
                label={formatEmailDisplay(email)}
              >
                <Space>
                  <MailOutlined />
                  <span>{email.name || email.email.split("@")[0]}</span>
                  <Text type="secondary">&lt;{email.email}&gt;</Text>
                </Space>
              </Option>
            ) : null;
          })}
        </Select.OptGroup>

        {/* Add a group for all available emails */}
        <Select.OptGroup label="All Available Emails">
          {Object.entries(availableEmails).map(([domain, emails]) =>
            emails.map((email) => (
              <Option
                key={email.email}
                value={email.email}
                label={formatEmailDisplay(email)}
              >
                <Space>
                  <MailOutlined />
                  <span>{email.name || email.email.split("@")[0]}</span>
                  <Text type="secondary">&lt;{email.email}&gt;</Text>
                </Space>
              </Option>
            ))
          )}
        </Select.OptGroup>
      </Select>
      {values.replyTo && !isValidEmail(getReplyToValue()) && (
        <Text type="danger">Please enter a valid email address</Text>
      )}
    </Form.Item>
  );

  // Update the modal content to handle dynamic columns
  const renderRecipientModal = () => (
    <Modal
      title="Recipient List"
      visible={isModalVisible}
      onOk={handleModalOk}
      onCancel={handleModalCancel}
      width={1200}
      style={{ top: 20 }}
      footer={[
        <Button key="back" onClick={handleModalCancel}>
          Close
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 24 }}>
        <Card title="Add New Recipient" size="small">
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              marginBottom: "16px",
            }}
          >
            {Array.from(recipientColumns).map((column) => {
              const columnDef = availableColumns.find(
                (col) => col.key === column
              );
              return (
                <Form.Item
                  key={column}
                  label={columnDef?.title || column}
                  style={{ margin: "0", flexBasis: "250px" }}
                  required={columnDef?.required}
                >
                  <Input
                    value={newRecipientData[column]}
                    onChange={(e) =>
                      setNewRecipientData((prev) => ({
                        ...prev,
                        [column]: e.target.value,
                      }))
                    }
                    placeholder={`Enter ${columnDef?.title || column}`}
                    status={
                      column === "email" &&
                      newRecipientData.email &&
                      !isValidEmail(newRecipientData.email)
                        ? "error"
                        : ""
                    }
                  />
                </Form.Item>
              );
            })}
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddRecipient}
          >
            Add Recipient
          </Button>
        </Card>
      </div>

      <Table
        columns={getTableColumns()}
        dataSource={values.externalRecipients}
        rowKey="email"
        scroll={{ x: "max-content", y: 400 }}
        pagination={{ pageSize: 10 }}
        size="small"
      />
    </Modal>
  );

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleModalOk = () => {
    setIsModalVisible(false);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const handleAddRecipient = () => {
    if (newRecipientData.email && isValidEmail(newRecipientData.email)) {
      const { email, ...otherFields } = newRecipientData;

      // Format the recipient according to schema
      const newRecipient = {
        email: email.toLowerCase(),
        values: {}, // Initialize empty values object
      };

      // Add all other fields to the values object
      Object.keys(otherFields).forEach((key) => {
        if (otherFields[key]) {
          // Only add non-empty values
          newRecipient.values[key] = otherFields[key];
        }
      });

      setValues((prevValues) => ({
        ...prevValues,
        externalRecipients: Array.isArray(prevValues.externalRecipients)
          ? [...prevValues.externalRecipients, newRecipient]
          : [newRecipient],
      }));

      // Reset form but maintain structure
      setNewRecipientData((prevData) => {
        const resetData = { ...prevData };
        Object.keys(resetData).forEach((key) => {
          resetData[key] = "";
        });
        return resetData;
      });
    } else {
      message.error("Please enter a valid email address");
    }
  };

  const handleRemoveRecipient = (email) => {
    setValues({
      ...values,
      externalRecipients: values.externalRecipients.filter(
        (r) => r.email !== email
      ),
    });
  };

  const getTableColumns = () => {
    const columns = Array.from(recipientColumns).map((key) => ({
      title: key, // Display exact key name
      dataIndex: key === "email" ? "email" : ["values", key],
      key: key,
      ellipsis: true,
      render: (text, record) => {
        if (key === "email") return record.email;
        return record.values?.[key] || "";
      },
    }));

    return [
      ...columns,
      {
        title: "Action",
        key: "action",
        width: 80,
        render: (_, record) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveRecipient(record.email)}
          />
        ),
      },
    ];
  };

  // Add email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle CC emails
  const handleCcInputChange = (e) => {
    const value = e.target.value;
    if (value.endsWith(",") || value.endsWith(" ")) {
      const email = value.slice(0, -1).trim();
      if (isValidEmail(email)) {
        setValues({
          ...values,
          cc: [...new Set([...values.cc, email])],
        });
        setCcInput("");
      }
    } else {
      setCcInput(value);
    }
  };

  const handleCcInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const email = ccInput.trim();
      if (isValidEmail(email)) {
        setValues({
          ...values,
          cc: [...new Set([...values.cc, email])],
        });
        setCcInput("");
      }
    }
  };

  const handleRemoveCc = (emailToRemove) => {
    setValues({
      ...values,
      cc: values.cc.filter((email) => email !== emailToRemove),
    });
  };

  // Handle BCC emails
  const handleBccInputChange = (e) => {
    const value = e.target.value;
    if (value.endsWith(",") || value.endsWith(" ")) {
      const email = value.slice(0, -1).trim();
      if (isValidEmail(email)) {
        setValues({
          ...values,
          bcc: [...new Set([...values.bcc, email])],
        });
        setBccInput("");
      }
    } else {
      setBccInput(value);
    }
  };

  const handleBccInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const email = bccInput.trim();
      if (isValidEmail(email)) {
        setValues({
          ...values,
          bcc: [...new Set([...values.bcc, email])],
        });
        setBccInput("");
      }
    }
  };

  const handleRemoveBcc = (emailToRemove) => {
    setValues({
      ...values,
      bcc: values.bcc.filter((email) => email !== emailToRemove),
    });
  };

  // Handle form submit
  const handleFormSubmit = (e) => {
    e.preventDefault();
    setUpdateLoading(true);

    // Validate mandatory fields
    if (
      !values.name ||
      !values.subject ||
      !values.htmlTemplate ||
      !values.campaignType ||
      !values.emailsToUse?.length ||
      !values.fromName
    ) {
      setUpdateLoading(false);
      return ErrorNotification("Please fill all the required fields");
    }

    // Additional validation for specific campaign types
    if (
      values.campaignType === "external" &&
      values.externalRecipients.length === 0
    ) {
      setUpdateLoading(false);
      return ErrorNotification("Please upload external recipients");
    }

    if (values.campaignType === "dateRange") {
      if (
        !values.dateRangeFilter?.startDate ||
        !values.dateRangeFilter?.endDate
      ) {
        setUpdateLoading(false);
        return ErrorNotification("Please select a date range for the campaign");
      }
      if (!dateRangeUsers.length) {
        setUpdateLoading(false);
        return ErrorNotification("No users found in the selected date range");
      }
    }

    // Prepare updated values for submission
    const updatedValues = {
      ...values,
      dateRangeUsers:
        values.campaignType === "dateRange"
          ? dateRangeUsers.map((user) => ({
              user: user._id,
              metadata: {
                email: user.email,
                name: user.name,
              },
            }))
          : [],
      excludedUsers:
        values.campaignType !== "targeted"
          ? excludedUsers.map((user) => user.value)
          : [],
      emailsToUse: selectedEmails.map((value) => {
        const [type, emailId, alias] = value.split(":");
        return {
          email: emailId,
          useAlias: type === "alias",
          selectedAlias: type === "alias" ? alias : null,
        };
      }),
      externalRecipients: values.externalRecipients.map((recipient) => ({
        ...recipient,
        values: new Map(Object.entries(recipient.values || {})),
      })),
    };

    updateEmailCampaignById(id, updatedValues)
      .then(() => {
        SuccessNotification(`Email campaign updated successfully`);
        setUpdateLoading(false);
        history.goBack();
      })
      .catch((error) => {
        setUpdateLoading(false);
        ErrorNotification("Error updating email campaign");
        console.error("UPDATE_EMAIL_CAMPAIGN_ERROR", error);
      });
  };

  const VariableForm = ({ variable, onSave, onCancel }) => {
    const [form] = Form.useForm();

    useEffect(() => {
      form.setFieldsValue(variable || {});
    }, [variable, form]);

    const handleSubmit = () => {
      form.validateFields().then((values) => {
        onSave(values);
        form.resetFields();
      });
    };

    return (
      <Modal
        title={variable ? "Edit Variable" : "Add Variable"}
        visible={variableModalVisible}
        onOk={handleSubmit}
        onCancel={onCancel}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="key"
            label="Variable Key"
            rules={[
              { required: true, message: "Please enter a variable key" },
              {
                pattern: /^[a-zA-Z][a-zA-Z0-9_]*$/,
                message:
                  "Key must start with a letter and contain only letters, numbers, and underscores",
              },
            ]}
            tooltip="This will be used in the template as {{variable_key}}"
          >
            <Input
              prefix={<CodeOutlined />}
              placeholder="e.g., company_name"
              disabled={!!variable}
            />
          </Form.Item>
          <Form.Item
            name="defaultValue"
            label="Default Value"
            rules={[
              { required: true, message: "Please enter a default value" },
            ]}
            tooltip="This value will be used if no specific value is provided"
          >
            <Input placeholder="Enter default value" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            tooltip="Helpful description of what this variable is used for"
          >
            <Input.TextArea
              placeholder="Describe what this variable represents..."
              rows={3}
            />
          </Form.Item>
        </Form>
      </Modal>
    );
  };

  // Add these handlers for variable management
  const handleAddVariable = () => {
    setEditingVariable(null);
    setVariableModalVisible(true);
  };

  const handleEditVariable = (variable) => {
    setEditingVariable(variable);
    setVariableModalVisible(true);
  };

  const handleSaveVariable = (variableData) => {
    setValues((prev) => {
      const newVariables = [...prev.variables];
      if (editingVariable) {
        // Edit existing variable
        const index = newVariables.findIndex(
          (v) => v.key === editingVariable.key
        );
        if (index !== -1) {
          newVariables[index] = variableData;
        }
      } else {
        // Add new variable
        newVariables.push(variableData);
      }
      return { ...prev, variables: newVariables };
    });
    setVariableModalVisible(false);
  };

  const handleDeleteVariable = (key) => {
    Modal.confirm({
      title: "Delete Variable",
      content: (
        <div>
          <p>Are you sure you want to delete the variable "{key}"?</p>
          <Alert
            message="Warning"
            description="This may affect any templates using this variable. Make sure to update your email template accordingly."
            type="warning"
            showIcon
          />
        </div>
      ),
      okText: "Delete",
      okType: "danger",
      onOk: () => {
        setValues((prev) => ({
          ...prev,
          variables: prev.variables.filter((v) => v.key !== key),
        }));
      },
    });
  };

  // Add this section to be inserted in your form
  const variableSection = (
    <Form.Item
      label="Template Variables"
      tooltip={{
        title:
          "Define variables that can be used in your email template using {{variable_key}} syntax",
        icon: <InfoCircleOutlined />,
      }}
    >
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Button
            type="dashed"
            onClick={handleAddVariable}
            icon={<PlusCircleOutlined />}
            block
          >
            Add Template Variable
          </Button>
        </div>

        {values.variables?.length > 0 ? (
          <Table
            dataSource={values.variables}
            rowKey="key"
            pagination={false}
            size="small"
            columns={[
              {
                title: "Variable Key",
                dataIndex: "key",
                key: "key",
                render: (text) => (
                  <Text copyable={{ text: `{{${text}}}` }}>
                    {`{{${text}}}`}
                  </Text>
                ),
              },
              {
                title: "Default Value",
                dataIndex: "defaultValue",
                key: "defaultValue",
              },
              {
                title: "Description",
                dataIndex: "description",
                key: "description",
                ellipsis: true,
              },
              {
                title: "Actions",
                key: "actions",
                width: 120,
                render: (_, record) => (
                  <Space>
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => handleEditVariable(record)}
                    />
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteVariable(record.key)}
                    />
                  </Space>
                ),
              },
            ]}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" align="center">
                <Text type="secondary">No template variables defined</Text>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  Add variables to personalize your email template
                </Text>
              </Space>
            }
          />
        )}

        <div style={{ marginTop: 16 }}>
          <Alert
            type="warning"
            showIcon
            message="Using Template Variables"
            description={
              <div>
                <p>
                  Use variables in your template by surrounding the key with
                  double curly braces:
                  <br />
                  <code>Hello {"{{first_name}}"}!</code>
                </p>
                <p>
                  If no specific value is provided, the default value will be
                  used.
                </p>
              </div>
            }
          />
        </div>
      </Card>

      <VariableForm
        variable={editingVariable}
        onSave={handleSaveVariable}
        onCancel={() => setVariableModalVisible(false)}
      />
    </Form.Item>
  );

  return (
    <Row>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "calc(80vh - 50px)",
            width: "100%",
          }}
        >
          <Spin />
        </div>
      ) : (
        <Col span={24}>
          <Form labelCol={{ span: 4 }} wrapperCol={{ span: 18 }}>
            {/* Campaign Type Selection */}
            <Form.Item label="Campaign Type" required>
              <Select
                name="campaignType"
                placeholder="Select campaign type"
                onChange={handleCampaignTypeChange}
                value={values.campaignType}
              >
                <Option value="all">All Users</Option>
                <Option value="planBased">Plan-Based</Option>
                <Option value="targeted">Targeted Users</Option>
                <Option value="external">External Recipients</Option>
                <Option value="dateRange">Date Range Based</Option>
              </Select>
            </Form.Item>
            {values.campaignType === "dateRange" && (
              <Form.Item
                label="Date Range"
                required
                tooltip={{
                  title:
                    "Select date range to target users based on their join date",
                  icon: <InfoCircleOutlined />,
                }}
              >
                <Card>
                  <Space
                    direction="vertical"
                    style={{ width: "100%" }}
                    size="large"
                  >
                    <RangePicker
                      style={{ width: "100%" }}
                      onChange={handleDateRangeChange}
                      value={
                        values.dateRangeFilter?.startDate &&
                        values.dateRangeFilter?.endDate
                          ? [
                              moment(values.dateRangeFilter.startDate),
                              moment(values.dateRangeFilter.endDate),
                            ]
                          : null
                      }
                      showTime
                      disabled={values.status !== "draft"}
                    />

                    {fetchingUserCount ? (
                      <Alert message="Fetching users..." type="info" showIcon />
                    ) : (
                      dateRangeUsers &&
                      dateRangeUsers?.length > 0 && (
                        <>
                          <Alert
                            message={`${dateRangeUsers?.length.toLocaleString()} users found in selected date range`}
                            type="success"
                            showIcon
                          />
                          <Card
                            size="small"
                            title="Users in Selected Date Range"
                            extra={
                              values.status === "draft" && (
                                <Button
                                  type="link"
                                  onClick={() => {
                                    const csvContent = Papa.unparse(
                                      dateRangeUsers.map((user) => ({
                                        Name: user.name,
                                        Email: user.email,
                                        "Join Date": moment(
                                          user.joinedAt
                                        ).format("YYYY-MM-DD HH:mm:ss"),
                                      }))
                                    );
                                    const blob = new Blob([csvContent], {
                                      type: "text/csv;charset=utf-8;",
                                    });
                                    const link = document.createElement("a");
                                    link.href = URL.createObjectURL(blob);
                                    link.download = `users_${moment().format(
                                      "YYYY-MM-DD"
                                    )}.csv`;
                                    link.click();
                                  }}
                                >
                                  Export to CSV
                                </Button>
                              )
                            }
                          >
                            <Table
                              columns={[
                                {
                                  title: "Name",
                                  dataIndex: "name",
                                  key: "name",
                                  render: (text) => <Text strong>{text}</Text>,
                                },
                                {
                                  title: "Email",
                                  dataIndex: "email",
                                  key: "email",
                                },
                                {
                                  title: "Join Date",
                                  dataIndex: "joinedAt",
                                  key: "joinedAt",
                                  render: (date) =>
                                    moment(date).format(
                                      "MMMM Do YYYY, h:mm:ss a"
                                    ),
                                },
                              ]}
                              dataSource={dateRangeUsers}
                              rowKey="_id"
                              size="small"
                              pagination={{
                                pageSize: 5,
                                showSizeChanger: true,
                                showTotal: (total, range) =>
                                  `${range[0]}-${range[1]} of ${total} users`,
                              }}
                              scroll={{ y: 240 }}
                            />
                          </Card>
                        </>
                      )
                    )}
                  </Space>
                </Card>
              </Form.Item>
            )}
            {/* Campaign Name */}
            <Form.Item label="Campaign Name" required>
              <Input
                name="name"
                placeholder="Enter campaign name"
                onChange={handleFormChange}
                value={values.name}
              />
            </Form.Item>
            <Form.Item
              label="From Name"
              required
              tooltip={{
                title: "The name that will appear as the sender of the email",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Input
                name="fromName"
                placeholder="Enter sender name"
                onChange={handleFormChange}
                value={values.fromName}
              />
            </Form.Item>
            {/* Email Subject */}
            <Form.Item label="Subject" required>
              <Input
                name="subject"
                placeholder="Enter email subject"
                onChange={handleFormChange}
                value={values.subject}
              />
            </Form.Item>
            {/* HTML Template Selection */}
            <Form.Item label="HTML Template" required>
              <Select
                name="htmlTemplate"
                placeholder="Select an HTML template"
                onChange={(value) =>
                  setValues({ ...values, htmlTemplate: value })
                }
                value={values.htmlTemplate}
              >
                {templates.map((template) => (
                  <Option key={template._id} value={template._id}>
                    {template.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {/* Variables */}
            {variableSection}
            {/* Conditional rendering based on campaign type */}
            {values.campaignType === "planBased" && (
              <Form.Item label="Included Plans">
                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  placeholder="Select plans to include"
                  onChange={(selectedPlans) =>
                    setValues({ ...values, includedPlans: selectedPlans })
                  }
                  value={values.includedPlans}
                >
                  {plans.map((plan) => (
                    <Option key={plan._id} value={plan._id}>
                      {plan.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            {values.campaignType === "targeted" && (
              <Form.Item label="Target Users">
                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  placeholder="Search and select users to target"
                  value={values.targetedUsers}
                  onChange={handleTargetedUsersChange}
                  onSearch={handleUserSearch}
                  loading={searching}
                  filterOption={false}
                >
                  {[...values.targetedUsers, ...userOptions].map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            {values.campaignType === "external" &&
              renderExternalRecipientsSection()}
            {values.campaignType !== "targeted" &&
              values.campaignType !== "external" && (
                <Form.Item label="Exclude Users">
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    placeholder="Search and select users to exclude"
                    value={excludedUsers.map((user) => user.value)}
                    onChange={handleExcludedUsersChange}
                    onSearch={handleUserSearch}
                    loading={searching}
                    filterOption={false}
                  >
                    {[...excludedUsers, ...userOptions].map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            {/* Scheduled Date */}
            <Form.Item label="Schedule For">
              <Space direction="vertical" style={{ width: "100%" }}>
                <DatePicker
                  showTime
                  style={{ width: "100%" }}
                  onChange={(date) =>
                    setValues({
                      ...values,
                      scheduledFor: date ? date.tz(timezone).format() : null,
                    })
                  }
                  value={
                    values.scheduledFor
                      ? moment.tz(values.scheduledFor, timezone)
                      : null
                  }
                  format={`YYYY-MM-DD HH:mm:ss [${timezone}]`}
                />
                <Select
                  value={timezone}
                  showSearch
                  style={{ width: "100%" }}
                  placeholder="Select a timezone"
                  onChange={(value) => setTimezone(value)}
                  options={moment.tz.names().map((tz) => ({
                    label: formatTimezone(tz),
                    value: tz,
                  }))}
                />
              </Space>
            </Form.Item>

            {/* Sending Emails */}
            <Form.Item
              label="Sending Emails"
              required
              tooltip={{
                title:
                  "Select email addresses and aliases to send this campaign from. Using multiple addresses can help improve delivery rates.",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Card>
                <Select
                  mode="multiple"
                  style={{ width: "100%", marginBottom: 16 }}
                  placeholder="Select emails and aliases to send from..."
                  value={selectedEmails}
                  onChange={handleEmailSelection}
                  optionLabelProp="label"
                  optionFilterProp="label"
                  options={formatEmailsForSelect()}
                  listHeight={400}
                  maxTagCount={5}
                  virtual={true}
                  dropdownStyle={{ minWidth: 400 }}
                  dropdownRender={(menu) => (
                    <div>
                      {menu}
                      <Divider style={{ margin: "8px 0" }} />
                      <Space style={{ padding: "0 8px 4px" }}>
                        <InfoCircleOutlined />
                        <Text type="secondary">
                          Tip: Using multiple sending addresses can improve
                          delivery rates
                        </Text>
                      </Space>
                    </div>
                  )}
                />

                {selectedEmails.length > 0 ? (
                  <Card
                    size="small"
                    title="Selected Sending Configuration"
                    bordered={false}
                    style={{ background: "#f5f5f5" }}
                  >
                    <Space
                      direction="vertical"
                      size="small"
                      style={{ width: "100%" }}
                    >
                      <Space>
                        <Badge
                          count={getSelectedEmailDetails().totalEmails}
                          style={{ backgroundColor: "#1890ff" }}
                        />
                        <Text>Total sending addresses selected</Text>
                      </Space>
                      <Space>
                        <Badge
                          count={getSelectedEmailDetails().baseEmails}
                          style={{ backgroundColor: "#52c41a" }}
                        />
                        <Text>Primary email addresses</Text>
                      </Space>
                      <Space>
                        <Badge
                          count={getSelectedEmailDetails().aliases}
                          style={{ backgroundColor: "#722ed1" }}
                        />
                        <Text>Email aliases</Text>
                      </Space>
                      <Space>
                        <Badge
                          count={getSelectedEmailDetails().domains.size}
                          style={{ backgroundColor: "#faad14" }}
                        />
                        <Text>Unique sending domains</Text>
                      </Space>
                    </Space>
                  </Card>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <Space direction="vertical" align="center">
                        <Text type="secondary">No sending emails selected</Text>
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          Select at least one email address or alias to send
                          from
                        </Text>
                      </Space>
                    }
                  />
                )}
              </Card>
            </Form.Item>
            {renderReplyToSection()}
            {/* Status */}
            <Form.Item label="Status">
              <Select
                name="status"
                onChange={(value) => setValues({ ...values, status: value })}
                value={values.status}
              >
                <Option value="draft">Draft</Option>
                <Option value="sending">Sending</Option>
                <Option value="completed">Completed</Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="CC"
              tooltip={{
                title:
                  "Enter email addresses for CC. Press Enter or use comma to add.",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Input
                value={ccInput}
                onChange={handleCcInputChange}
                onKeyDown={handleCcInputKeyDown}
                placeholder="Enter email addresses"
              />
              {values.cc?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {values.cc.map((email) => (
                    <Tag
                      key={email}
                      closable
                      onClose={() => handleRemoveCc(email)}
                      style={{ margin: "4px" }}
                    >
                      {email}
                    </Tag>
                  ))}
                </div>
              )}
            </Form.Item>

            {/* BCC */}
            <Form.Item
              label="BCC"
              tooltip={{
                title:
                  "Enter email addresses for BCC. Press Enter or use comma to add.",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Input
                value={bccInput}
                onChange={handleBccInputChange}
                onKeyDown={handleBccInputKeyDown}
                placeholder="Enter email addresses"
              />
              {values.bcc?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {values.bcc.map((email) => (
                    <Tag
                      key={email}
                      closable
                      onClose={() => handleRemoveBcc(email)}
                      style={{ margin: "4px" }}
                    >
                      {email}
                    </Tag>
                  ))}
                </div>
              )}
            </Form.Item>

            {/* Email Sending Volume */}
            <Form.Item
              label="Email Sending Volume"
              tooltip="Control the sending volume for this campaign."
            >
              <EmailVolumeControl
                totalRecipients={
                  values.campaignType === "external"
                    ? values.externalRecipients?.length
                    : values.campaignType === "dateRange"
                    ? dateRangeUsers?.length
                    : values.campaignType === "planBased"
                    ? values.totalEmails || 1
                    : values.totalEmails || 0
                }
                initialMinutesBetweenEmails={
                  values.emailSendingConfig?.minutesBetweenEmails || 5
                }
                customInterval={
                  values.emailSendingConfig?.customInterval || false
                }
                emailsPerHour={values.emailSendingConfig?.emailsPerHour || 12}
                senderCount={values.emailsToUse?.length || 1}
                initialSendingMode={
                  values.emailSendingConfig?.sendingMode || "time_based"
                }
                initialEmailsPerMinute={
                  values.emailSendingConfig?.emailsPerMinute || 20
                }
                onVolumeChange={(config) => {
                  setValues((prevValues) => ({
                    ...prevValues,
                    emailSendingConfig: {
                      ...prevValues.emailSendingConfig,
                      ...config,
                    },
                  }));
                }}
              />
            </Form.Item>

            {/* Submit button */}
            <Form.Item wrapperCol={{ span: 12, offset: 4 }}>
              <Button
                type="primary"
                onClick={handleFormSubmit}
                loading={updateLoading}
              >
                Update Campaign
              </Button>
            </Form.Item>
          </Form>

          {/* Modal for viewing and managing recipients */}
          {renderRecipientModal()}
        </Col>
      )}
    </Row>
  );
};

export default withRouter(EmailCampaignUpdateForm);
